import type React from "react"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  ArrowLeft,
  Save,
  X,
} from "lucide-react"
import { Button } from "../../components/ui/button"
import FirmInformationCard from "../../components/forms/FirmInformationCard"
import { firmsApi, CreateFirmData } from "../../services/firmsApi"

interface Firm {
  first_name: string
  last_name: string
  email: string
  law_firm: string
  state: string
}

interface FormErrors {
  [key: string]: string[]
}

export default function AddFirm() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})

  // Email validation states
  const [emailChecking, setEmailChecking] = useState(false)
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null)
  const [emailMessage, setEmailMessage] = useState("")

  const [formData, setFormData] = useState<Firm>({
    first_name: "",
    last_name: "",
    email: "",
    law_firm: "",
    state: "",
  })

  // Debounced email availability check
  useEffect(() => {
    if (!formData.email || formData.email.length < 3) {
      setEmailAvailable(null)
      setEmailMessage("")
      return
    }

    // Use a single timer for all validation (format + availability)
    const timer = setTimeout(() => {
      // First validate email format
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
      
      if (!emailRegex.test(formData.email)) {
        setEmailAvailable(false)
        setEmailMessage("Please enter a valid email address")
        return
      }

      // Additional email format checks
      const [localPart, domain] = formData.email.split('@')
      if (localPart.length > 64) {
        setEmailAvailable(false)
        setEmailMessage("Email local part cannot exceed 64 characters")
        return
      }
      if (domain.length > 253) {
        setEmailAvailable(false)
        setEmailMessage("Email domain cannot exceed 253 characters")
        return
      }
      if (localPart.length === 0) {
        setEmailAvailable(false)
        setEmailMessage("Email local part cannot be empty")
        return
      }
      if (domain.length === 0) {
        setEmailAvailable(false)
        setEmailMessage("Email domain cannot be empty")
        return
      }

      // Only call API if email format is valid
      const checkAvailability = async () => {
        try {
          setEmailChecking(true)
          const response = await firmsApi.checkFirmEmailAvailability(formData.email)
          setEmailAvailable(response.available)
          setEmailMessage(response.message)
        } catch (error) {
          console.error('Email availability check failed:', error)
          setEmailAvailable(null)
          setEmailMessage("Unable to verify email")
        } finally {
          setEmailChecking(false)
        }
      }

      checkAvailability()
    }, 500) // 500ms delay for all validation

    return () => clearTimeout(timer)
  }, [formData.email])

  const handleInputChange = (field: keyof Firm, value: string) => {
    const processedValue = field === 'email' ? value.replace(/\s+$/, '') : value
    setFormData((prev) => ({ ...prev, [field]: processedValue }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: [] }))
    }
    
    // Reset validation states when email changes
    if (field === 'email') {
      setEmailAvailable(null)
      setEmailMessage("")
    }

    // Real-time validation
    const newErrors = { ...errors }
    
    // First name validation
    if (field === 'first_name') {
      if (processedValue.length > 50) {
        newErrors.first_name = ["First name cannot exceed 50 characters"]
      } else if (newErrors.first_name) {
        delete newErrors.first_name
      }
    }
    
    // Last name validation
    if (field === 'last_name') {
      if (processedValue.length > 50) {
        newErrors.last_name = ["Last name cannot exceed 50 characters"]
      } else if (newErrors.last_name) {
        delete newErrors.last_name
      }
    }
    
    // Email validation - only check length, format validation is handled by useEffect
    if (field === 'email') {
      if (processedValue.length > 254) {
        newErrors.email = ["Email cannot exceed 254 characters"]
      } else if (newErrors.email) {
        delete newErrors.email
      }
    }
    
    // Law firm validation
    if (field === 'law_firm') {
      if (processedValue.length > 255) {
        newErrors.law_firm = ["Law firm name cannot exceed 255 characters"]
      } else if (newErrors.law_firm) {
        delete newErrors.law_firm
      }
    }
    
    setErrors(newErrors)
  }

  // Check if form can be submitted
  const canSubmit = () => {
    return formData.first_name.trim() && 
           formData.last_name.trim() && 
           formData.email.trim() && 
           formData.law_firm.trim() &&
           formData.state.trim() &&
           emailAvailable !== false && // Email not invalid (can be true or null)
           !Object.keys(errors).length
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setErrors({})

    try {
      // Validate form data
      const newErrors: FormErrors = {}
      
      // First name validation
      if (!formData.first_name.trim()) {
        newErrors.first_name = ["First name is required"]
      } else if (formData.first_name.length > 50) {
        newErrors.first_name = ["First name cannot exceed 50 characters"]
      }
      
      // Last name validation
      if (!formData.last_name.trim()) {
        newErrors.last_name = ["Last name is required"]
      } else if (formData.last_name.length > 50) {
        newErrors.last_name = ["Last name cannot exceed 50 characters"]
      }
      
      // Email validation (required)
      if (!formData.email.trim()) {
        newErrors.email = ["Email is required"]
      }
      
      // Law firm validation
      if (!formData.law_firm.trim()) {
        newErrors.law_firm = ["Law firm is required"]
      } else if (formData.law_firm.length > 255) {
        newErrors.law_firm = ["Law firm name cannot exceed 255 characters"]
      }
      
      // State validation
      if (!formData.state.trim()) {
        newErrors.state = ["State is required"]
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors)
        setLoading(false)
        return
      }

      // Check email availability before submitting
      if (emailAvailable !== true) {
        setErrors(prev => ({ ...prev, email: ["Please ensure the email is available before submitting"] }))
        setLoading(false)
        return
      }

      // Prepare data for API
      const firmData: CreateFirmData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        law_firm: formData.law_firm.trim(),
        state: formData.state.trim(),
      }

      // Send POST request to API
      const response = await firmsApi.createFirm(firmData)

      if (!response.error) {
        // Success - navigate back
        navigate("/app/firms")
      } else {
        // Handle API errors
        setErrors({ general: [response.message || "Failed to create firm"] })
      }
    } catch (error: any) {
      console.error("Error saving firm:", error)
      
      // Handle different types of errors
      if (error.message?.includes("email") || error.message?.includes("Email")) {
        setErrors({ email: ["This email address is already in use. Please use a different email."] })
      } else if (error.message?.includes("400")) {
        // Try to parse backend validation errors
        try {
          const errorData = JSON.parse(error.message)
          if (errorData.errors) {
            const backendErrors: FormErrors = {}
            Object.keys(errorData.errors).forEach(key => {
              if (Array.isArray(errorData.errors[key])) {
                backendErrors[key] = errorData.errors[key]
              } else {
                backendErrors[key] = [errorData.errors[key]]
              }
            })
            setErrors(backendErrors)
            return
          }
        } catch (parseError) {
          // If we can't parse the error, fall back to general error
        }
        setErrors({ general: ["Bad Request (400) - Please check your input data"] })
      } else {
        setErrors({ general: ["Failed to create firm. Please try again."] })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate("/app/firms")
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={handleCancel} className="text-slate-600 hover:text-slate-900">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Firms
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Add New Firm</h1>
          <p className="text-slate-600">Enter the firm information below</p>
        </div>

        {/* Error Message */}
        {errors.general && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800">{errors.general[0]}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <FirmInformationCard
            firmData={formData}
            errors={errors}
            onDataChange={handleInputChange}
            alwaysEditable={true}
            emailValidation={{
              emailChecking,
              emailAvailable,
              emailMessage
            }}
          />
          <div className="flex justify-end gap-2 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
              className="border-slate-300 hover:border-slate-400"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || !canSubmit()}
              className="bg-slate-900 hover:bg-slate-800 text-white disabled:bg-slate-400 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? "Saving..." : "Save Firm"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
} 